import { createSlice } from '@reduxjs/toolkit';
import { DataGridState } from '../../@types/datagrid';
import { GridFilterModel, GridLogicOperator, GridSortModel } from '@mui/x-data-grid';

export enum DataGridKey {
  CampaignList = 'CampaignList',
  Employees = 'Employees',
  ThrottlingGroups = 'ThrottlingGroups',
  ThrottlingGroupCampaigns = 'ThrottlingGroupCampaigns',
  OrganizationsList = 'OrganizationsList',
  CampaignMembersResponses = 'CampaignMembersResponses',
  CampaignMembersDelivered = 'CampaignMembersDelivered',
  CampaignMembersScheduled = 'CampaignMembersScheduled',
  CampaignMembersUnsubscribed = 'CampaignMembersUnsubscribed',
  CampaignMembersFailed = 'CampaignMembersFailed',
  CampaignMembersExpired = 'CampaignMembersExpired',
  Companies = 'Companies',
  Etrack1Maps = 'Etrack1Maps',
  IVRList = 'IVRList',
  LogoList = 'LogoList',
  SendersList = 'SendersList',
  BrandedLinksList = 'BrandedLinksList',
  OrganizationUsers = 'OrganizationUsers',
  PGapList = 'PGapList',
  PGapAddEmployees = 'PGapAddEmployees',
  Reports = 'Reports',
  Respondents = 'Respondents',
  Reviews = 'Reviews',
  TemplateList = 'TemplateList',
  Orders = 'Orders',
  OrderTypes = 'OrderTypes',
  TenantOrders = 'TenantOrders',
  NpsNotifications = 'NpsNotifications',
  AiTokenUsage = 'AiTokenUsage'
}

type getFilterModelProps = {
  field: string;
  operator: string;
};
const getFilterModel = ({ field, operator }: getFilterModelProps): GridFilterModel => {
  return {
    items: [
      {
        field,
        operator
      }
    ],
    logicOperator: GridLogicOperator.And,
    quickFilterValues: [],
    quickFilterLogicOperator: GridLogicOperator.And
  };
};

type DataGridFiltering = { [key in DataGridKey]: GridFilterModel };
export const defaultFiltering: DataGridFiltering = {
  [DataGridKey.NpsNotifications]: getFilterModel({
    field: 'name',
    operator: 'contains'
  }),
  [DataGridKey.OrganizationsList]: getFilterModel({
    field: 'organizationName',
    operator: 'contains'
  }),
  [DataGridKey.Companies]: getFilterModel({
    field: 'name',
    operator: 'contains'
  }),
  [DataGridKey.Etrack1Maps]: getFilterModel({
    field: 'name',
    operator: 'contains'
  }),
  [DataGridKey.IVRList]: getFilterModel({
    field: 'name',
    operator: 'contains'
  }),
  [DataGridKey.LogoList]: getFilterModel({
    field: 'fileName',
    operator: 'contains'
  }),
  [DataGridKey.SendersList]: getFilterModel({
    field: 'sender',
    operator: 'contains'
  }),
  [DataGridKey.BrandedLinksList]: getFilterModel({
    field: 'name',
    operator: 'contains'
  }),
  [DataGridKey.OrganizationUsers]: getFilterModel({
    field: 'name',
    operator: 'contains'
  }),
  [DataGridKey.PGapList]: getFilterModel({
    field: 'campaign/name',
    operator: 'contains'
  }),
  [DataGridKey.ThrottlingGroupCampaigns]: getFilterModel({
    field: 'name',
    operator: 'contains'
  }),
  [DataGridKey.PGapAddEmployees]: getFilterModel({
    field: 'firstName',
    operator: 'contains'
  }),
  [DataGridKey.Reports]: getFilterModel({
    field: 'name',
    operator: 'contains'
  }),
  [DataGridKey.Respondents]: getFilterModel({
    field: 'name',
    operator: 'contains'
  }),
  [DataGridKey.Reviews]: getFilterModel({
    field: '',
    operator: ''
  }),
  [DataGridKey.CampaignList]: getFilterModel({
    field: 'name',
    operator: 'contains'
  }),
  [DataGridKey.Employees]: getFilterModel({
    field: 'firstName',
    operator: 'contains'
  }),
  [DataGridKey.TemplateList]: getFilterModel({
    field: 'name',
    operator: 'contains'
  }),
  [DataGridKey.ThrottlingGroups]: getFilterModel({
    field: 'name',
    operator: 'contains'
  }),
  [DataGridKey.CampaignMembersResponses]: getFilterModel({
    field: 'respondent/firstName',
    operator: 'contains'
  }),
  [DataGridKey.CampaignMembersDelivered]: getFilterModel({
    field: 'respondent/firstName',
    operator: 'contains'
  }),
  [DataGridKey.CampaignMembersScheduled]: getFilterModel({
    field: 'respondent/firstName',
    operator: 'contains'
  }),
  [DataGridKey.CampaignMembersUnsubscribed]: getFilterModel({
    field: 'respondent/firstName',
    operator: 'contains'
  }),
  [DataGridKey.CampaignMembersFailed]: getFilterModel({
    field: 'respondent/firstName',
    operator: 'contains'
  }),
  [DataGridKey.CampaignMembersExpired]: getFilterModel({
    field: 'respondent/firstName',
    operator: 'contains'
  }),
  [DataGridKey.Orders]: getFilterModel({
    field: 'description',
    operator: 'contains'
  }),
  [DataGridKey.OrderTypes]: getFilterModel({
    field: 'key',
    operator: 'contains'
  }),
  [DataGridKey.TenantOrders]: getFilterModel({
    field: 'orderTypeName',
    operator: 'contains'
  }),
  [DataGridKey.AiTokenUsage]: getFilterModel({
    field: 'tenantId',
    operator: 'contains'
  })
};

type GridSortItem = GridSortModel[number];

type DataGridSorting = { [key in DataGridKey]: GridSortItem[] };
export const defaultSorting: DataGridSorting = {
  [DataGridKey.OrganizationsList]: [{ field: 'organizationName', sort: 'asc' }],
  [DataGridKey.Companies]: [{ field: 'name', sort: 'asc' }],
  [DataGridKey.Etrack1Maps]: [{ field: 'name', sort: 'asc' }],
  [DataGridKey.IVRList]: [{ field: 'name', sort: 'asc' }],
  [DataGridKey.LogoList]: [],
  [DataGridKey.SendersList]: [{ field: 'sender', sort: 'asc' }],
  [DataGridKey.BrandedLinksList]: [{ field: 'name', sort: 'asc' }],
  [DataGridKey.OrganizationUsers]: [{ field: 'name', sort: 'asc' }],
  [DataGridKey.PGapList]: [{ field: 'campaign/name', sort: 'asc' }],
  [DataGridKey.PGapAddEmployees]: [{ field: 'firstName', sort: 'asc' }],
  [DataGridKey.Reports]: [{ field: 'name', sort: 'asc' }],
  [DataGridKey.Respondents]: [{ field: 'firstName', sort: 'asc' }],
  [DataGridKey.Reviews]: [],
  [DataGridKey.CampaignList]: [{ field: 'created', sort: 'desc' }],
  [DataGridKey.Employees]: [{ field: 'firstName', sort: 'asc' }],
  [DataGridKey.TemplateList]: [{ field: 'created', sort: 'desc' }],
  [DataGridKey.ThrottlingGroups]: [{ field: 'name', sort: 'asc' }],
  [DataGridKey.ThrottlingGroupCampaigns]: [{ field: 'name', sort: 'asc' }],
  [DataGridKey.CampaignMembersResponses]: [{ field: 'response/rated', sort: 'desc' }],
  [DataGridKey.Orders]: [{ field: 'active', sort: 'desc' }],
  [DataGridKey.OrderTypes]: [{ field: 'key', sort: 'asc' }],
  [DataGridKey.TenantOrders]: [{ field: 'status', sort: 'asc' }],
  [DataGridKey.NpsNotifications]: [{ field: 'name', sort: 'desc' }],

  //The below tables cannot yet sort on the scheduled time (or other datetime values taken from the sms/email tables)
  [DataGridKey.CampaignMembersDelivered]: [{ field: 'id', sort: 'desc' }], //surveySendDate
  [DataGridKey.CampaignMembersScheduled]: [{ field: 'id', sort: 'desc' }], //surveyScheduleDate
  [DataGridKey.CampaignMembersUnsubscribed]: [{ field: 'id', sort: 'desc' }], //surveyScheduleDate
  [DataGridKey.CampaignMembersFailed]: [{ field: 'id', sort: 'desc' }], //surveyScheduleDate
  [DataGridKey.CampaignMembersExpired]: [{ field: 'id', sort: 'desc' }],
  [DataGridKey.AiTokenUsage]: [{ field: 'totalTokens', sort: 'desc' }]
};

export const initialState: DataGridState = {
  density: {},
  sorting: defaultSorting,
  filtering: defaultFiltering,
  columnVisibility: {},
  columnVisibilityModel: {
    id: false,
    edit: true
  },
  pinnedColumns: {},
  columnOrder: {}
};

export const persistsVersion = 3;
// Function to apply default updates to state
const getDefaultState = (state: any) => {
  return {
    ...state,
    sorting: defaultSorting,
    filtering: defaultFiltering,
    columnVisibilityModel: {
      id: false,
      edit: true
    }
  };
};
export const migrations = {
  //updated initial sorting on all tables
  0: (state: any) => getDefaultState(state),
  //updated initial sorting on all tables - to ensure changes for CampaignsList columns are applied
  1: (state: any) => getDefaultState(state),
  //ensure that any sorting/filtering on datagrid 'edit' columns are reset
  2: (state: any) => getDefaultState(state),
  //switch campaign members defaults to respondent field paths
  3: (state: any) => getDefaultState(state)
};

const slice = createSlice({
  name: 'datagrid',
  initialState,
  reducers: {
    setDensity(state, action) {
      state.density[action.payload[0]] = action.payload[1];
    },

    setSorting(state, action) {
      state.sorting[action.payload[0]] = action.payload[1];
    },

    setFiltering(state, action) {
      state.filtering[action.payload[0]] = action.payload[1];
    },

    setColumnVisibility(state, action) {
      state.columnVisibility[action.payload[0]] = action.payload[1];
    },

    setPinnedColumns(state, action) {
      state.pinnedColumns[action.payload[0]] = action.payload[1];
    },

    setColumnOrder(state, action) {
      const dataGridKey = action.payload[0];
      state.columnOrder[dataGridKey] = action.payload[1];
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const {
  setDensity,
  setSorting,
  setFiltering,
  setColumnVisibility,
  setPinnedColumns,
  setColumnOrder
} = slice.actions;
